// LIDControlDialog.js
import { useState } from 'react'
import { Typography, Button } from '@mui/material'
import LIDControlSurfaceForm from './LIDControlSurfaceForm'
import LIDControlDrainForm from './LIDControlDrainForm'
import LIDControlSoilForm from './LIDControlSoilForm'
import LIDControlDrainMatForm from './LIDControlDrainMatForm'
import LIDControlStorageForm from './LIDControlStorageForm'
import LIDControlPavementForm from './LIDControlPavementForm'
import { UniversalDropDown } from '@fileops/swmm-react'
import "../../css/DemoCode.css"

/**
 * Frame for data inputs. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @returns UI for interactive swmm modeling.
 */
export default function LIDControlDialog({ model, setModel, target, setNewName, fullModel, setFullModel }) {
  const [newTargetName, setNewTargetName] = useState(target)
  const [controlType, setControlType] = useState(model.LID_CONTROLS[target].Type)
  const [controlTypes, setControlTypes] = useState(['VS', 'GR', 'PP', 'BC', 'RG', 'IT', 'RB', 'RD'])
  let LID_CONTROLS = {
    SURFACE: {
      StorHt: '1',
      VegFrac: '0.0',
      Rough: '0.1',
      Slope: '1.0',
      Xslope: '5'
    },
    SOIL: {
      Thick: '0',
      Por: '0.5',
      FC: '0.2',
      WP: '0.1',
      Ksat: '0.5',
      Kcoeff: '10.0',
      Suct: '3.5'
    },
    PAVEMENT: {
      Thick: '0',
      Vratio: '0.15',
      FracImp: '0',
      Perm: '100',
      Vclog: '0',
      regenInterval: '0',
      regenFrac: '0'
    },
    STORAGE: {
      Height: '0',
      Vratio: '0.75',
      Seepage: '0.5',
      Vclog: '0', 
      Covered: 'NO'
    },
    DRAIN: {
      Coeff: '0',
      Expon: '0.5',
      Offset: '6',
      Delay: '0',
      Open: '0',
      Close: '0',
      Curve: ''
    },
    DRAINMAT: {
      Thick: '3',
      Vratio: '0.5',
      Rough: '0.1'
    }
  }

  // Save the data depending upon the type of LID:
  const saveEdits = () => {
    setFullModel(prev => ({
      ...prev, 
      LID_CONTROLS: {
        ...prev.LID_CONTROLS, 
        [target]: {
          ...(['BC', 'RG', 'GR', 'IT', 'PP', 'RD', 'VS'].includes(model.LID_CONTROLS[target].Type) && {SURFACE: model.LID_CONTROLS[target].SURFACE}),
          ...(['BC', 'RG', 'GR', 'PP']                  .includes(model.LID_CONTROLS[target].Type) && {SOIL:    model.LID_CONTROLS[target].SOIL}),
          ...(['PP']                                    .includes(model.LID_CONTROLS[target].Type) && {PAVEMENT:model.LID_CONTROLS[target].PAVEMENT}),
          ...(['BC', 'RG', 'IT', 'PP', 'RB']            .includes(model.LID_CONTROLS[target].Type) && {STORAGE: model.LID_CONTROLS[target].STORAGE}),
          ...(['BC', 'IT', 'PP', 'RD', 'RB']            .includes(model.LID_CONTROLS[target].Type) && {DRAIN:   model.LID_CONTROLS[target].DRAIN}),
          ...(['GR']                                    .includes(model.LID_CONTROLS[target].Type) && {DRAINMAT:model.LID_CONTROLS[target].DRAINMAT}),
          ...((model.LID_CONTROLS[target].REMOVALS) && {REMOVALS: model.LID_CONTROLS[target].REMOVALS}),
          Type: model.LID_CONTROLS[target].Type
        }
      }
    }))
  }

  function updateControlType(val) {
    // Don't let any undefined updates go through.
    if(val === undefined) val = 'VS'
    
    // if there is not a set of values for a type like this, create one:
    if(model &&
      model.LID_CONTROLS &&
      model.LID_CONTROLS[target] ){
        setModel(prev => ({
        ...prev,
        LID_CONTROLS: {
          ...prev.LID_CONTROLS,
          [target]: {
            ...LID_CONTROLS,
            ...prev.LID_CONTROLS[target],
            Type: val
          }
        } 
      }))
    }

    // Close any open tabs
    if(val !== 'RB'){
      openTab(null, 'Surface')
    } else {
      openTab(null, 'Storage')
    }
    
    setControlType(val)
  }

  function handleChange(e) {
    setNewName(e.target.value)
    setNewTargetName(e.target.value)
  }

  function openTab(evt, tabName) {
    var i, tabcontent, tablinks

    // Get all elements with class="tabcontent" and hide them
    tabcontent = document.getElementsByClassName("tabcontent")
    for (i = 0; i < tabcontent.length; i++) {
      tabcontent[i].style.display = "none"
    }

    // Get all elements with class="tablinks" and remove the class "active"
    tablinks = document.getElementsByClassName("tablinks")
    for (i = 0; i < tablinks.length; i++) {
      tablinks[i].className = tablinks[i].className.replace(" active", "")
    }

    if(document.getElementById(tabName) && document.getElementById(tabName+'_tab')){
      document.getElementById(tabName).style.display = "block"
      document.getElementById(tabName+'_tab').classList.add("active")
    }
  }

  /**
   * If there is a valid model, create a UI for the model.
   */
  return(
    <>
    {
      <>
        <Typography id="modal-modal-title" variant="p4" >
          <div style={{display:"flex", flexDirection: "column", padding: '10px'}}>
            <label>Lid Controls</label>
            <input style={{fontSize: '16px', background: 'rgb(50, 50, 50)', color: 'lightskyblue', border: 'none', textAlign: 'center'}} type="text" value={newTargetName} onChange={handleChange} />
          </div>
        </Typography>
        <div>
          <Button fullWidth onClick={saveEdits} >Save Edits</Button>
        </div>
        {
          model.LID_CONTROLS &&
          <>
          <label className='editor-small' style={{paddingBottom: '10px'}}>Control Type:
            <UniversalDropDown style={{padding: '0'}}
              IDs={controlTypes} 
              selectText={"Select a Control Type"} 
              value={controlType}
              onChange={(val)=>{updateControlType(val)}} >
            </UniversalDropDown>
          </label>
          </>
        }
        <div style={{height: "280px", background:'black', overflowY: 'hidden', paddingBottom:"120px"}}>
          {
            <>
            <div className="tab">
              {(
                ['BC', 'RG', 'GR', 'IT', 'PP', 'RD', 'VS'].includes(controlType) &&
                <button id="Surface_tab" className="tablinks active" onClick={(e)=>openTab(e, 'Surface' )}>Surface</button>
              )}
              {(
                ['BC', 'IT', 'PP', 'RB', 'RD'].includes(controlType) &&
                <button id="Drain_tab" className="tablinks" onClick={(e)=>openTab(e, 'Drain' )}>Drain</button>
              )}
              {(
                ['BC', 'RG', 'GR', 'PP'].includes(controlType) &&
                <button id="Soil_tab" className="tablinks" onClick={(e)=>openTab(e, 'Soil' )}>Soil</button>
              )}
              {(
                ['BC', 'RG', 'IT', 'PP', 'RB'].includes(controlType) &&
                <button id="Storage_tab" className="tablinks" onClick={(e)=>openTab(e, 'Storage' )}>Storage</button>
              )}
              {(
                ['GR'].includes(controlType) &&
                <button id="DrainMat_tab" className="tablinks" onClick={(e)=>openTab(e, 'DrainMat' )}>Drain Mat</button>
              )}
              {(
                ['PP'].includes(controlType) &&
                <button id="Pavement_tab" className="tablinks" onClick={(e)=>openTab(e, 'Pavement' )}>Pavement</button>
              )}
            </div>
            
            {(
              ['BC', 'RG', 'GR', 'IT', 'PP', 'RD', 'VS'].includes(controlType) &&
              <div id="Surface" style={{display: 'block'}} className="tabcontent">
                {<LIDControlSurfaceForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            
            {(
              ['BC', 'IT', 'PP', 'RB', 'RD'].includes(controlType) &&
              <div id="Drain" className="tabcontent">
                {<LIDControlDrainForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            
            {(
              ['BC', 'RG', 'GR', 'PP'].includes(controlType) &&
              <div id="Soil" className="tabcontent">
                {<LIDControlSoilForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            
            {(
              ['BC', 'RG', 'IT', 'PP', 'RB'].includes(controlType) &&
              <div id="Storage" className="tabcontent">
                {<LIDControlStorageForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            
            {(
              ['GR'].includes(controlType) &&
              <div id="DrainMat" className="tabcontent">
                {<LIDControlDrainMatForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            
            {(
              ['PP'].includes(controlType) &&
              <div id="Pavement" className="tabcontent">
                {<LIDControlPavementForm model={model} setModel={setModel} setFullModel={setFullModel} target={target} targetType={'LID_CONTROLS'} controlType={controlType} />}
              </div>
            )}
            </>
          }
        </div>
        </>
      }
    </>
  )
} 